import React, { useState } from 'react';
import { useNavigate } from "react-router-dom";
import { useUserContext } from '../../app/protectedRoute/UserContext';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { LogOut, Bell, Menu, X } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Link } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';

const PostLoginHeader: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useUserContext();
  const { role, user } = state;
  const [logoutDialogOpen, setLogoutDialogOpen] = useState<boolean>(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const { notifications, unreadCount, clearAll, markRead } = useNotifications();

  const handleLogoClick = () => {
    if (role) {
      switch (role) {
        case 'host':
          navigate('/home');
          break;
        case 'guest':
          navigate('/guest-search-results');
          break;
        default:
          navigate('/');
      }
    } else {
      navigate('/');
    }
    setMobileMenuOpen(false);
  };

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
    setMobileMenuOpen(false);
  };

  const handleConfirmLogout = () => {
    dispatch({ type: 'RESET_USER' });
    localStorage.clear();
    navigate('/');
    setLogoutDialogOpen(false);
  };

  const getDashboardLink = () => {
    switch (role) {
      case 'host': return '/home';
      case 'guest': return '/dashboard';
      case 'admin': return '/admin/dashboard';
      default: return '/';
    }
  };

  const getDashboardText = () => {
    switch (role) {
      case 'host': return 'Host Dashboard';
      case 'guest': return 'Guest Dashboard';
      case 'admin': return 'Admin Dashboard';
      default: return 'Dashboard';
    }
  };

  const notifBell = (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => { setNotifOpen(o => !o); markRead(); }}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {notifOpen && (
        <div>
          <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <span className="font-semibold text-sm text-gray-800">Notifications</span>
              {notifications.length > 0 && (
                <button className="text-xs text-[#276859] hover:underline" onClick={clearAll}>
                  Clear all
                </button>
              )}
            </div>
            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-400">No notifications yet</div>
              ) : (
                notifications.map(n => (
                  <div key={n.id} className="px-4 py-3 border-b border-gray-50 hover:bg-gray-50">
                    <p className="text-sm font-medium text-gray-800">{n.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{n.body}</p>
                    <p className="text-[10px] text-gray-400 mt-1">{n.time.toLocaleTimeString()}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const navLinks = (
    <>
      <Link to={getDashboardLink()} className="text-foreground font-medium hover:text-primary transition-colors" onClick={() => setMobileMenuOpen(false)}>
        {getDashboardText()}
      </Link>
      {notifBell}
      <Button variant="ghost" className="text-muted-foreground hover:text-primary" onClick={handleLogoutClick}>
        <LogOut className="h-4 w-4 mr-2" />
        Logout
      </Button>
      <Avatar className="h-9 w-9 border-2 border-primary">
        <AvatarImage src={user?.avatar} />
        <AvatarFallback className="bg-primary text-primary-foreground text-sm">
          {user?.first_name?.charAt(0)}
        </AvatarFallback>
      </Avatar>
    </>
  );

  const mobileNavLinks = (
    <>
      <Button variant="ghost" className="text-muted-foreground hover:text-primary" onClick={handleLogoutClick}>
        <LogOut className="h-4 w-4 mr-2" />
        Logout
      </Button>
    </>
  );

  return (
    <>
      <header className="sticky top-0 z-50 bg-gradient-to-r from-secondary/20 to-secondary/10 border-b border-border">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <span className="text-2xl md:text-3xl font-bold italic text-foreground">Social Bunkr</span>
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            {navLinks}
          </nav>

          {/* Mobile: bell + menu */}
          <div className="flex items-center gap-2 md:hidden">
            {notifBell}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 flex flex-col items-center">
              {mobileNavLinks}
            </div>
          </div>
        )}
      </header>

      <Dialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Logout</DialogTitle>
            <DialogDescription>Are you sure you want to log out?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogoutDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleConfirmLogout}>Logout</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PostLoginHeader;
